import { useMemo } from 'react';
import {
  UtilizationDashboardDto,
  MonthlyUserTrendsDto,
  UserRankingDto,
  UserRankingsResponseDto,
} from '@repo/shared/dtos/analytics/UtilizationDto';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { BASE_URLS } from './config';
import { normalizeAnalyticsFilters, createDefaultBaseFilters, createAnalyticsHook } from './utils';

interface UtilizationFilters extends BaseAnalyticsFilter {
  monthsBack?: string;
  orderBy?: 'sessions' | 'minutes';
  limit?: string;
  page?: string;
  pageSize?: string;
}

const useUtilizationQuery = createAnalyticsHook({
  baseUrl: BASE_URLS.UTILIZATION,
  queryKeyPrefix: 'utilization',
});

export const useUtilizationDashboard = (filters?: BaseAnalyticsFilter) =>
  useUtilizationQuery<UtilizationDashboardDto>('/dashboard', filters);

export const useMonthlyUserTrends = (filters?: BaseAnalyticsFilter, monthsBack?: number) => {
  const queryFilters = useMemo(
    () =>
      ({
        ...normalizeAnalyticsFilters(filters, createDefaultBaseFilters()),
        ...(monthsBack && { monthsBack: monthsBack.toString() }),
      }) as UtilizationFilters,
    [filters, monthsBack],
  );

  return useUtilizationQuery<MonthlyUserTrendsDto[]>('/monthly-trends', queryFilters);
};

export const useUserRankings = (
  filters?: BaseAnalyticsFilter,
  orderBy: 'sessions' | 'minutes' = 'sessions',
  limit?: number,
  page?: number,
  pageSize?: number,
) => {
  const queryFilters = useMemo(
    () =>
      ({
        ...normalizeAnalyticsFilters(filters, createDefaultBaseFilters()),
        orderBy,
        ...(limit && { limit: limit.toString() }),
        ...(page && { page: page.toString() }),
        ...(pageSize && { pageSize: pageSize.toString() }),
      }) as UtilizationFilters,
    [filters, orderBy, limit, page, pageSize],
  );

  const query = useUtilizationQuery<UserRankingsResponseDto>('/user-rankings', queryFilters);

  return useMemo(
    () => ({
      ...query,
      data: query.data?.users ?? [],
      analysisWindow: query.data?.analysis_window,
    }),
    [query],
  );
};

export const useUserRankingsThisMonth = (filters?: BaseAnalyticsFilter, limit?: number) => {
  const queryFilters = useMemo(() => {
    const normalized = normalizeAnalyticsFilters(filters, createDefaultBaseFilters());
    const {
      startDate: _sd,
      endDate: _ed,
      ...rest
    } = normalized as BaseAnalyticsFilter & Record<string, unknown>;
    return {
      ...(rest as BaseAnalyticsFilter),
      ...(limit && { limit: limit.toString() }),
    } as UtilizationFilters;
  }, [filters, limit]);

  return useUtilizationQuery<UserRankingDto[]>('/top-users-this-month', queryFilters);
};

export const useUtilizationOverview = (filters?: BaseAnalyticsFilter) => {
  const dashboardData = useUtilizationDashboard(filters);
  const topUsersAllTimeData = useUserRankings(filters, 'sessions', 4);
  const topUsersThisMonthData = useUserRankingsThisMonth(filters, 5);

  const overview = useMemo(() => {
    if (!dashboardData.data) return null;

    const kpis = dashboardData.data.kpis;
    const topUsersAllTime = topUsersAllTimeData.data?.slice(0, 4) || [];
    const topUsersThisMonth = topUsersThisMonthData.data || [];

    return {
      totalUsers: kpis.total_users,
      usersThisMonth: kpis.users_this_month,
      newUsersThisMonth: kpis.new_users_this_month,
      returningUsersThisMonth: kpis.returning_users_this_month,
      returnUserRate: kpis.return_user_rate,
      usersInPeriod: kpis.users_in_period,
      newUsersInPeriod: kpis.new_users_in_period,
      returningUsersInPeriod: kpis.returning_users_in_period,
      returnUserRateInPeriod: kpis.return_user_rate_in_period,
      topUsersAllTime,
      topUsersThisMonth,
    };
  }, [dashboardData.data, topUsersAllTimeData.data, topUsersThisMonthData.data]);

  return {
    data: overview,
    isLoading:
      dashboardData.isLoading || topUsersAllTimeData.isLoading || topUsersThisMonthData.isLoading,
    isError: dashboardData.isError || topUsersAllTimeData.isError || topUsersThisMonthData.isError,
    error: dashboardData.error || topUsersAllTimeData.error || topUsersThisMonthData.error,
    hasData: Boolean(overview),
    isEmpty: !overview,
  };
};
